/**
 * Tunnel validation utilities
 */
import { dispatchMutation, dispatchQuery } from '../../../../utils/dispatch.utils';
import { ahuApi } from '../../../../services';
import { DEFAULT_COEFFICIENTS } from '../../../../models';

/**
 * Helper function to extract number from error message
 * Matches backbone extract_number functionality
 */
export function extractNumber(message: string): number | null {
  if (!message) return null;

  // Try to find numbers in the message (including decimals)
  const match = message.match(/(\d+\.?\d*)/);
  if (match && match[1]) {
    return parseFloat(match[1]);
  }

  return null;
}

/**
 * Validates tunnel and increases dimensions if necessary
 * Reusable function that matches backbone validate_tunnel and compile_errors functionality
 */
export async function validateAndFixTunnelDimensions(
  tunnelId: number,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
  currentTunnel?: any,
): Promise<{ isCompiled: boolean; updatedTunnel?: any }> {
  let isCompiled = false;
  let updatedTunnel: any = undefined;

  try {
    const error: any = await dispatchQuery(
      ahuApi.endpoints.validateTunnel,
      tunnelId,
      getOptions(),
    );

    if (error?.validation_error_messages?.errors_count) {
      // Use provided tunnel or fetch it if not available
      const tunnel =
        currentTunnel ||
        (await dispatchQuery(
          ahuApi.endpoints.getTunnel,
          tunnelId,
          getOptions(),
        ));

      // Compile errors to extract width/height increases
      const tunnel_width: number[] = [0];
      const tunnel_height: number[] = [0];

      if (error?.validation_error_messages?.sections) {
        error.validation_error_messages.sections.forEach((section: any) => {
          if (section.components) {
            section.components.forEach((component: any) => {
              if (component.message) {
                if (component.message.includes('Increase Tunnel Width')) {
                  const extractedNumber = extractNumber(component.message);
                  if (extractedNumber !== null) {
                    tunnel_width.push(extractedNumber);
                  }
                } else if (
                  component.message.includes('Increase Tunnel Height')
                ) {
                  const extractedNumber = extractNumber(component.message);
                  if (extractedNumber !== null) {
                    tunnel_height.push(extractedNumber);
                  }
                }
              }
            });
          }
        });
      }

      const max_width = Math.max(...tunnel_width);
      const max_height = Math.max(...tunnel_height);

      if (
        (max_height > 0 && tunnel.height !== max_height) ||
        (max_width > 0 && tunnel.width !== max_width)
      ) {
        updatedTunnel = await dispatchMutation(
          ahuApi.endpoints.updateAhuTunnel,
          {
            id: tunnelId,
            body: {
              height: Math.ceil(+tunnel.height + max_height),
              width: Math.ceil(+tunnel.width + max_width),
            },
          },
          getOptions(),
        );
      }

      onProgress?.(DEFAULT_COEFFICIENTS.compile_errors); // compile_errors coefficient
      isCompiled = true;
    }
  } catch (e: any) {
    // Ignore validation errors, but check for stop signal
    if (e.statusText === 'abort') {
      throw e;
    }
  }
  // Return the updated tunnel if it was updated, otherwise return the current tunnel passed in
  // This ensures callers always have the latest tunnel data to update their arrays
  return { isCompiled, updatedTunnel: updatedTunnel || currentTunnel };
}
